import React, { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaBars,
  FaChevronLeft,
  FaChevronRight,
  FaChevronDown,
  FaChevronUp,
  FaHome,
  FaTimes,
  FaSignOutAlt,
} from "react-icons/fa";

import {
  mobileHeader,
  mobileTitle,
  sidebarContainer,
  sidebarHeader,
  brandContainer,
  brandTitle,
  brandSubtitle,
  sidebarToggle,
  navContainer,
  activeNavItem,
  inactiveNavItem,
  activeIcon,
  inactiveIcon,
  itemLabel,
  footerContainer,
  backdrop,
} from './masterPageStyles';

const isSamePath = (currentPath, itemPath) =>
  currentPath === String(itemPath || "").split("?")[0].toLowerCase();

const ModuleSidebar = ({ title, subtitle, mobileLabel, sections = [] }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Set to TRUE so the sidebar is open by default on first load
  const [isOpen, setIsOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [submenuOpen, setSubmenuOpen] = useState(true);
  const [expandedMobileSection, setExpandedMobileSection] = useState(null);

  // Sidebar expansion depends on your manual click toggle
  const sidebarExpanded = isOpen;
  const showLabels = mobileOpen || sidebarExpanded;

  const currentPath = useMemo(() => location.pathname.toLowerCase(), [location.pathname]);
  
  const activeSection = useMemo(
    () =>
      (sections || []).find((section) =>
        (section?.items || []).some((item) => isSamePath(currentPath, item.path))
      ) || sections?.[0],
    [currentPath, sections]
  );

  const goToItem = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  const handleLogout = () => {
    navigate("/"); 
  };

  return (
    <>
      {/* Mobile Header Bar */}
      <div className={`${mobileHeader} md:hidden fixed top-0 left-0 w-full z-50`}>
        <h2 className={mobileTitle}>{mobileLabel}</h2>
        <button type="button" onClick={() => setMobileOpen((value) => !value)}>
          {mobileOpen ? <FaTimes size={20} /> : <FaBars size={20} />}
        </button>
      </div>

      {/* Sidebar Drawer Container (Default open, hover removed, manual toggle active) */}
      <aside
        className={`${sidebarContainer} ${
          mobileOpen ? "translate-x-0 w-44 pt-1" : "-translate-x-full md:translate-x-0 pt-0"
        } md:translate-x-0 ${sidebarExpanded ? "w-44" : "w-10"} transition-all duration-300 fixed md:relative h-screen flex flex-col justify-between z-40`}
      >
        <div className="flex-1 overflow-y-auto">
          {/* Brand Header with Manual Toggle Button */}
          <div className={`${sidebarHeader} hidden md:flex`}>
            {sidebarExpanded && (
              <div className={brandContainer}>
                <span className={brandTitle}>{title}</span>
                {subtitle && <span className={brandSubtitle}>{subtitle}</span>}
              </div>
            )}
            <button
              type="button"
              aria-label="Toggle sidebar"
              className={sidebarToggle}
              onClick={() => setIsOpen((value) => !value)}
            >
              <FaBars size={14} />
            </button>
          </div>

          {/* Navigation Links */}
          <nav className={`${navContainer} pt-1`}>
            {(sections || []).map((section) => {
              const SectionIcon = section.icon || section.items?.[0]?.icon || FaHome;
              const isActiveSection = activeSection?.id === section.id;
              const hasMultipleItems = section.items && section.items.length > 1;
              const isMobileAccordionOpen = expandedMobileSection === section.id;

              return (
                <div key={section.id} className="flex flex-col">
                  <button
                    type="button"
                    title={section.title}
                    onClick={() => {
                      setSubmenuOpen(true);
                      if (hasMultipleItems && mobileOpen) {
                        setExpandedMobileSection(isMobileAccordionOpen ? null : section.id);
                      } else {
                        if (section.items?.[0]) goToItem(section.items[0].path);
                      }
                    }}
                    className={isActiveSection ? activeNavItem : inactiveNavItem}
                  >
                    <SectionIcon
                      size={14}
                      className={isActiveSection ? activeIcon : inactiveIcon}
                    />
                    {showLabels && <span className={itemLabel}>{section.title}</span>}
                    {hasMultipleItems && mobileOpen && (
                      <span className="ml-auto">
                        {isMobileAccordionOpen ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                      </span>
                    )}
                  </button>

                  {/* Mobile Submenu Dropdown */}
                  {mobileOpen && hasMultipleItems && isMobileAccordionOpen && (
                    <div className="ml-4 mt-1 flex flex-col space-y-1 border-l-2 border-[#00b4d8] pl-2">
                      {section.items.map((subItem) => {
                        const SubIcon = subItem.icon || FaHome;
                        const isSubActive = isSamePath(currentPath, subItem.path);

                        return (
                          <button
                            key={subItem.path}
                            type="button"
                            onClick={() => goToItem(subItem.path)}
                            className={`flex items-center gap-2 py-1 px-2 rounded text-[11px] font-semibold uppercase text-left transition-all ${
                              isSubActive
                                ? "bg-[#00b4d8] text-white"
                                : "text-slate-300 hover:bg-slate-800 hover:text-white"
                            }`}
                          >
                            <SubIcon size={11} />
                            <span>{subItem.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Logout Button Footer */}
        <div className={`${footerContainer} shrink-0 bg-[#0a2540] border-t border-slate-800`}>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 w-full p-2 rounded text-red-600 hover:bg-red-50 transition-colors font-medium text-xs"
            title="Logout"
          >
            <FaSignOutAlt size={12} className="shrink-0 text-red-600" />
            {showLabels && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Desktop Top Submenu Bar */}
      <div
        className={`fixed right-0 top-0 z-40 hidden border-b border-slate-200 bg-white shadow-sm md:block transition-all duration-300 ${
          sidebarExpanded ? "left-44" : "left-10"
        }`}
      >
        <div className="flex min-h-8 items-center gap-2 px-2">
          <button
            type="button"
            className="inline-flex h-5 w-5 items-center justify-center rounded bg-[#00b4d8] text-white shadow-xs"
            aria-label="Toggle submenu bar"
            onClick={() => setSubmenuOpen((value) => !value)}
          >
            {submenuOpen ? <FaChevronLeft size={10} /> : <FaChevronRight size={10} />}
          </button>

          {submenuOpen && (
            <div className="flex flex-1 items-center gap-1.5 overflow-x-auto py-1">
              {(activeSection?.items || []).map((item) => {
                const Icon = item.icon || FaHome;
                const isActive = isSamePath(currentPath, item.path);

                return (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => goToItem(item.path)}
                    className={`inline-flex py-0.5 shrink-0 items-center gap-1 rounded px-1.5 text-[14px] font-medium border transition-all ${
                      isActive
                        ? "bg-[#00b4d8] text-white  "
                        : "bg-slate-50 text-slate-800 border-slate-300 hover:bg-sky-50 hover:text-[#00b4d8] hover:border-[#00b4d8]"
                    }`}
                  >
                    <Icon size={12} />
                    {item.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {mobileOpen && <div onClick={() => setMobileOpen(false)} className={backdrop} />}
    </>
  );
};

export default ModuleSidebar;